import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import { updateMeta } from 'portal-modules/messages/redux/modules/meta'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import dedupe from 'portal-utils/dedupe'
import {
  generateAlerts,
  getAlerts,
  dismissSelectedAlerts,
  deleteSelectedAlerts,
} from '../../api/api'

// action types
export const DISMISS_ALERTS = 'rp/alerts/DISMISS_ALERTS'
export const ALERTS_DISMISSED = 'rp/alerts/ALERTS_DISMISSED'
export const REQUEST_ALERTS = 'rp/alerts/REQUEST_ALERTS'
export const REQUEST_ALERT_LIST = 'rp/alerts/REQUEST_ALERT_LIST'
export const RECEIVE_ALERTS = 'rp/alerts/RECEIVE_ALERTS'
export const DELETE_ALERTS = 'rp/alerts/DELETE_ALERTS'
export const ALERTS_DELETED = 'rp/alerts/ALERTS_DELETED'
const ALERTS_DISMISSED_BY_TYPE_BY_REF =
  'rp/alerts/ALERTS_DISMISSED_BY_TYPE_BY_REF'

// Priority constants (1 = high, 4 = low/default)
const DEFAULT_PRIORITY = 4

// reducer
export default function alerts(state = [], { type, payload }) {
  switch (type) {
    case LOGOUT:
      return []
    case RECEIVE_ALERTS:
      return dedupe([...payload], 'id')
    case ALERTS_DISMISSED:
      return state.map(alert =>
        payload.includes(alert.id) ? { ...alert, dismissed: true } : alert,
      )
    case ALERTS_DISMISSED_BY_TYPE_BY_REF:
      return state.map(alert =>
        payload.refId == alert.reference_id &&
        payload.type == alert.alert_type_id
          ? { ...alert, dismissed: true }
          : alert,
      )
    case ALERTS_DELETED:
      return state.filter(({ id }) => !payload.includes(id))
    default:
      return state
  }
}

// action creators
export const requestAlerts = () => ({ type: REQUEST_ALERTS })

export const requestAlertList = () => ({ type: REQUEST_ALERT_LIST })

// payload = [id]
export const dismissAlerts = (payload, callback) => ({
  type: DISMISS_ALERTS,
  callback,
  payload,
})

// payload = [id]
export const dismissAlertsByTypeByRef = (payload, callback) => ({
  type: ALERTS_DISMISSED_BY_TYPE_BY_REF,
  callback,
  payload,
})

// payload = [id]
export const deleteAlerts = (payload, callback) => ({
  type: DELETE_ALERTS,
  callback,
  payload,
})

// sagas
export function* fetchAlerts() {
  const meta = yield select(({ meta }) => meta.alerts)
  const { updated_count } = yield call(generateAlerts)
  const alerts = yield call(getAlerts, meta)

  // Ensure alerts is an array and handle potential API response variations
  const alertsArray = Array.isArray(alerts) ? alerts : []
  
  const sortedAlerts = alertsArray.sort((a, b) => (a.priority || DEFAULT_PRIORITY) - (b.priority || DEFAULT_PRIORITY))

  if (Array.isArray(alerts)) {
    yield all([
      put(
        updateMeta({
          key: 'messageCounts',
          updates: { alerts: +updated_count },
        }),
      ),
      put({
        type: RECEIVE_ALERTS,
        // make sure to sort by priority (1 = high, 4 = low, etc.)
        payload: sortedAlerts,
      }),
    ])
  }
}

export function* fetchAlertsList() {
  const meta = yield select(({ meta }) => meta.alerts)
  const alerts = yield call(getAlerts, meta)
  const sortedAlerts = Array.isArray(alerts)
    ? alerts.sort((a, b) => (a.priority || DEFAULT_PRIORITY) - (b.priority || DEFAULT_PRIORITY))
    : []
  if (Array.isArray(alerts)) {
    yield put({
      type: RECEIVE_ALERTS,
      // make sure to sort by priority (1 = high, 4 = low, etc.)
      payload: sortedAlerts,
    })
  }
}

export function* postDismissAlerts({ payload, callback }) {
  const { message, updated_count } = yield call(dismissSelectedAlerts, {
    ids: payload,
  })
  let error

  if (message) {
    error = __('Delete failed. Please try again.')
  } else {
    yield all([
      put({ type: ALERTS_DISMISSED, payload }),
      put(
        updateMeta({
          key: 'messageCounts',
          updates: { alerts: +updated_count },
        }),
      ),
    ])
  }

  typeof callback === 'function' && callback(error)
}

export function* postDeleteAlerts({ payload, callback }) {
  const { message, updated_count } = yield call(deleteSelectedAlerts, {
    ids: payload,
  })
  let error

  if (message) {
    error = __('Delete failed. Please try again.')
  } else {
    yield all([
      put({ type: ALERTS_DELETED, payload }),
      put(
        updateMeta({
          key: 'messageCounts',
          updates: { alerts: +updated_count },
        }),
      ),
    ])
  }

  typeof callback === 'function' && callback(error)
}

export function* watchRequestAlerts() {
  yield takeLatest(
    REQUEST_ALERTS,
    createRequestSaga(fetchAlerts, {
      key: 'alerts',
      message: __('Checking for alerts'),
    }),
  )
}

export function* watchRequestAlertList() {
  yield takeLatest(
    REQUEST_ALERT_LIST,
    createRequestSaga(fetchAlertsList, {
      key: 'alerts',
      message: __('Checking for alerts'),
    }),
  )
}

export function* watchDismissAlerts() {
  yield takeLatest(
    DISMISS_ALERTS,
    createRequestSaga(postDismissAlerts, {
      key: 'alerts',
      message: __('Dismissing alerts'),
      write: true,
    }),
  )
}

export function* watchDeleteAlerts() {
  yield takeLatest(
    DELETE_ALERTS,
    createRequestSaga(postDeleteAlerts, {
      key: 'alerts',
      message: __('Deleting alerts'),
      write: true,
    }),
  )
}

export const sagas = [watchRequestAlerts, watchDismissAlerts, watchDeleteAlerts, watchRequestAlertList]
